import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { PortalProtectedRoute } from '../components/portal/PortalProtectedRoute';
import { PortalGate } from '../components/portal/PortalGate';

// Portal auth + public rating pages are only ever visited on a /portal/* or
// /rate/:token URL, but a static import here pulls them (and portalApi,
// usePortalAuth, etc.) into the eager main-entry bundle that every admin
// operator downloads before /login too. Code-split them like the
// authenticated portal pages below — App.tsx's top-level Suspense already
// covers the brief loading gap.
const SatisfactionRatingPage = lazy(() => import('../pages/public/SatisfactionRatingPage'));
const PortalLoginPage = lazy(() => import('../pages/portal/PortalLoginPage'));
const PortalForgotPasswordPage = lazy(() => import('../pages/portal/PortalForgotPasswordPage'));
const PortalResetPasswordPage = lazy(() => import('../pages/portal/PortalResetPasswordPage'));
const PortalInviteSetupPage = lazy(() => import('../pages/portal/PortalInviteSetupPage'));

// Authenticated portal pages are code-split into a portal-only chunk group.
const PortalDashboardPage = lazy(() => import('../pages/portal/PortalDashboardPage'));
const PortalInvoicesPage = lazy(() => import('../pages/portal/PortalInvoicesPage'));
const PortalSubscriptionsPage = lazy(() => import('../pages/portal/PortalSubscriptionsPage'));
const PortalTicketsPage = lazy(() => import('../pages/portal/PortalTicketsPage'));
const PortalTicketDetailPage = lazy(() => import('../pages/portal/PortalTicketDetailPage'));
const PortalNewTicketPage = lazy(() => import('../pages/portal/PortalNewTicketPage'));
const PortalPaymentMethodsPage = lazy(() => import('../pages/portal/PortalPaymentMethodsPage'));
const PortalPaymentsPage = lazy(() => import('../pages/portal/PortalPaymentsPage'));
const PortalProfilePage = lazy(() => import('../pages/portal/PortalProfilePage'));

/**
 * Customer portal routes and public routes.
 *
 * Portal routes (K6): All wrapped in <PortalGate> which shows the maintenance
 * page when the portal kill-switch is disabled (customer_portal.enabled=false).
 * Literal path segments come before param routes (e.g. 'new' before ':uuid').
 */
export const portalRoutes: RouteObject[] = [
  // Public routes (no auth required)
  {
    path: '/rate/:token',
    element: <SatisfactionRatingPage />,
  },

  // Customer portal — public auth pages
  {
    path: '/portal',
    element: <PortalGate><Navigate to="/portal/login" replace /></PortalGate>,
  },
  {
    path: '/portal/login',
    element: <PortalGate><PortalLoginPage /></PortalGate>,
  },
  {
    path: '/portal/forgot-password',
    element: <PortalGate><PortalForgotPasswordPage /></PortalGate>,
  },
  {
    path: '/portal/reset-password/:token',
    element: <PortalGate><PortalResetPasswordPage /></PortalGate>,
  },
  {
    path: '/portal/invite/:token/setup',
    element: <PortalGate><PortalInviteSetupPage /></PortalGate>,
  },

  // Customer portal — protected pages
  {
    path: '/portal/dashboard',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalDashboardPage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  {
    path: '/portal/invoices',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalInvoicesPage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  {
    path: '/portal/subscriptions',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalSubscriptionsPage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  // K4 — Literal 'new' must come before ':uuid' param route.
  {
    path: '/portal/tickets/new',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalNewTicketPage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  {
    path: '/portal/tickets/:uuid',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalTicketDetailPage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  {
    path: '/portal/tickets',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalTicketsPage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  // K5 — Payment methods (Stripe SetupIntent flow + list + remove)
  {
    path: '/portal/payment-methods',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalPaymentMethodsPage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  // #406 — profile (view + self-service edit: phone, language, marketing consent)
  {
    path: '/portal/profile',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalProfilePage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  // #406 — payment history
  {
    path: '/portal/payments',
    element: (
      <PortalGate>
        <PortalProtectedRoute>
          <PortalPaymentsPage />
        </PortalProtectedRoute>
      </PortalGate>
    ),
  },
  {
    path: '/portal/*',
    element: <PortalGate><Navigate to="/portal/login" replace /></PortalGate>,
  },
];
